import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { PlusIcon, TrashIcon } from "@/assets/icons";
import { ONLY_NUMBER_REGEX } from "@/constants/regex";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

const TEST_ID_PREFIX = "input-entry";

type InputEntryProps = {
  maxItem: number;
  fieldName: string;
  placeHolder: string;
  showTrashIcon?: boolean;
  onChange?: () => void;
  invalidValues?: (string | number)[];
  errorMessage?: string;
  testId?: string;
  maxValue?: number;
};

const InputEntry = ({
  maxItem = 3,
  fieldName,
  placeHolder,
  showTrashIcon = true,
  onChange,
  invalidValues = [],
  errorMessage = "",
  testId = TEST_ID_PREFIX,
  maxValue = 100000
}: InputEntryProps) => {
  const { control, setValue, register } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: fieldName // unique name for your Field Array
  });

  const fieldValues = useWatch({ name: fieldName, control });
  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (ONLY_NUMBER_REGEX.test(e.key)) e.preventDefault();
  };

  const isInvalidValue = (val: unknown) => {
    if (val === undefined || val === null) return false;

    return invalidValues.some((invalid) => {
      return String(invalid).trim() === String(val).trim();
    });
  };

  return (
    <div className="flex flex-col gap-3 items-center w-full">
      {fields.map((field, index) => {
        const isInvalid = isInvalidValue(fieldValues?.[index]);

        return (
          <div
            key={field.id} // important to include key with field's id
            className="w-full flex flex-col gap-1"
          >
            <div
              className={twMerge(
                "flex justify-between w-full gap-2 border rounded-[12px] h-[40px] overflow-hidden items-center px-3 ",
                isInvalid
                  ? "border-red-600"
                  : "border-gainsboro focus-within:border-teal-500"
              )}
            >
              <input
                className="w-full input-el"
                placeholder={placeHolder}
                type="number"
                min={0}
                max={maxValue}
                onKeyDown={handleKeydown}
                {...register(`${fieldName}.${index}`, {
                  onChange: (e) => {
                    const target = e.target as HTMLInputElement;
                    const value: number | "" =
                      target.value === ""
                        ? ""
                        : Math.min(
                            Math.max(Number(target.value), 0),
                            maxValue ?? Number(target.value)
                          );

                    target.value = value === "" ? "" : String(value);

                    setValue(`${fieldName}.${index}`, String(value), {
                      shouldValidate: true,
                      shouldDirty: true
                    });

                    onChange?.();
                  }
                })}
              />
              {/* Trash Icon */}
              {showTrashIcon && (
                <TrashIcon
                  className="fill-light-gray w-[20px] cursor-pointer"
                  onClick={() => {
                    remove(index);
                    onChange?.();
                  }}
                  data-testid={`${testId}-${index}-trash-icon`}
                />
              )}
            </div>
            {isInvalid && (
              <div className="text-[10px] text-red-600 ml-1">
                {errorMessage}
              </div>
            )}
          </div>
        );
      })}
      {/* Add more input */}
      <Button
        className="h-[40px] w-fit btn-default !border-none"
        type="button"
        disabled={fields.length === maxItem}
        onClick={() => {
          append(" ");
          onChange?.();
        }}
        data-testid={`${testId}-add-more-button`}
      >
        <PlusIcon width={12} />
        <span className="text-sm font-bold">Add More</span>
      </Button>
    </div>
  );
};

export default InputEntry;
